import React, { MouseEventHandler } from 'react';
import { Modifier } from '../../../../../lib/bemUtils/Modifier';

import bem from '../../../../../lib/bemUtils/bem';

import { ButtonIconActionConfirm, ButtonIconActionDecline } from '../../../Btn/ButtonWrapper';

import { Heading, HeadingHierarchy } from '../../../../../_lib/components/Heading/Heading';
import { StatusInfoForms } from '../../../../../_lib/components/StatusInfo';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import { LinkingPages } from './partials/LinkingPages';
import { Hint } from '../../../../../_lib/components/Hint';
import { LinkingPageType } from '../../../../../types/api/LinkingPageType';
import LiveRegion from '../../../../atoms/LiveRegion/LiveRegion';
import { StatusInfoModifier } from '../../../../../_lib/components/StatusInfo/partials/StatusInfo';

type FormSubmitOrCancelProps = {
  interactionStatusText?: string;
  status?: 'success' | 'fail' | 'sending' | '';
  statusModifier?: Modifier<StatusInfoModifier>;
  ariaControls?: string;
  hideSubmitButton?: boolean;
  disableSubmitButton?: boolean;
  focusOnMount?: boolean;
  hint?: string;
  headline: string | React.ReactNode;
  headlineId?: string;
  hierarchy: HeadingHierarchy;
  hideHeadline?: boolean;
  acceptButtonText?: string;
  acceptButtonType?: 'button' | 'submit';
  acceptFormAction?: string;
  acceptAction?: MouseEventHandler<HTMLButtonElement>;
  cancelButtonText?: string;
  cancelButtonType?: 'button' | 'submit';
  cancelFormAction?: string;
  cancelAction?: MouseEventHandler<HTMLButtonElement>;
  disableCancelButton?: boolean;
  onlySubmit?: boolean;
  linkingPages?: LinkingPageType;
};

/**
 * Renders two Buttons
 */
function FormSubmitOrCancel({
  interactionStatusText,
  status,
  statusModifier,
  ariaControls,
  hideSubmitButton,
  disableSubmitButton,
  focusOnMount,
  hint,
  headline,
  headlineId,
  hierarchy,
  hideHeadline,
  acceptButtonText,
  acceptButtonType = 'submit',
  acceptFormAction,
  acceptAction,
  cancelButtonText,
  cancelButtonType = 'button',
  cancelFormAction,
  cancelAction,
  disableCancelButton,
  onlySubmit,
  linkingPages,
}: FormSubmitOrCancelProps) {
  const { t } = useSonTranslation('FormSubmitOrCancel');

  /**
   * Will return two buttons wrapped into a <Fragment>
   */
  const renderButtons = () => {
    return (
      <>
        {!hideSubmitButton && (
          <div className="form-submit-or-cancel__button-container">
            <ButtonIconActionConfirm
              text={acceptButtonText || t('yes')}
              type={acceptButtonType}
              formAction={acceptFormAction}
              onClick={acceptAction}
              ariaDisabled={status === 'success' || status === 'sending' || disableSubmitButton}
              ariaControls={ariaControls}
              focusOnMount={focusOnMount}
            />
          </div>
        )}
        {!onlySubmit && (
          <div className="form-submit-or-cancel__button-container">
            <ButtonIconActionDecline
              text={cancelButtonText || t('no')}
              type={cancelButtonType}
              formAction={cancelFormAction}
              onClick={cancelAction}
              ariaDisabled={status === 'success' || status === 'sending' || disableCancelButton}
              ariaControls={ariaControls}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="form-submit-or-cancel">
      {hint && <Hint hint={t('hint')} message={hint} />}
      {linkingPages && <LinkingPages linkingPages={linkingPages} />}
      {status !== 'success' && (
        <div className="form-submit-or-cancel__container">
          <Heading
            hierarchy={hierarchy}
            id={headlineId}
            className={bem('form-submit-or-cancel__headline', hideHeadline ? 'invisible' : '')}
          >
            <span className="form-submit-or-cancel__headline-content">{headline}</span>
          </Heading>
          <div className={onlySubmit ? 'form-submit-or-cancel__buttons-only-submit' : 'form-submit-or-cancel__buttons'}>
            {renderButtons()}
          </div>
        </div>
      )}
      <div className="form-submit-or-cancel__interaction-status">
        <LiveRegion invisible={false} ariaAtomic>
          {status && (
            <div className="form-submit-or-cancel__status">
              <StatusInfoForms type={status} message={interactionStatusText} modifier={statusModifier} />
            </div>
          )}
        </LiveRegion>
      </div>
    </div>
  );
}

export default FormSubmitOrCancel;
